/**
 * AuthGuard.tsx — mango-web
 * PURPOSE: Route protection component. Redirects unauthenticated users to /login.
 * Mirrors Android's OnboardingViewModel fast-path auth check.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

// --- Component ---

/**
 * PURPOSE: Wrap routes that require authentication.
 * - Connected to: stores/authStore.ts → reads user + isInitialized
 * - Used by: App.tsx → wraps all non-auth routes
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, isInitialized, isLoading } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (isInitialized && !user) {
      console.debug('[AuthGuard] not authenticated, redirecting to /login');
      navigate('/login', { replace: true });
    }
  }, [isInitialized, user, navigate]);

  // --- Negative states first ---
  if (!isInitialized || isLoading) {
    return (
      <div className="auth-guard-loading">
        <div className="auth-guard-spinner" />
      </div>
    );
  }

  if (!user) return null;

  return <>{children}</>;
}

// --- Related Files ---
// CONNECTED TO:
//   - src/stores/authStore.ts — reads auth state
// USED BY:
//   - src/App.tsx — wraps protected routes
